import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, forkJoin, map } from 'rxjs';
import _ from 'lodash';

import { MacroGrid, Menu } from 'src/app/models';

import { BaseService } from '../base.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { ApiType } from 'src/app/pos.config';
import {
  POSManagerProduct,
  POSManagerService,
  POSProduct,
  POSProductEditDto,
  PosQuantityRestriction
} from 'src/app/models/pos-manager.model';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';
import { AppStateHelper } from 'src/app/store/app/app.state.helper';

@Injectable({
  providedIn: 'root'
})
export class MenuService extends BaseService {
  constructor(
    store: Store,
    private readonly http: HttpClient,
    private readonly errorService: ErrorService
  ) {
    super(store);
  }

  getMacroGrids(): Observable<MacroGrid[]> {
    return this.http
      .get<{ macro_grids: MacroGrid[] }>(this.getLocationUrl('macro_grids'))
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.macro_grids || [])
      );
  }

  getMacroGridDetail(
    meal_id: number,
    macro_grid_id: number
  ): Observable<MacroGrid> {
    return this.http
      .get<MacroGrid>(
        this.getLocationUrl(`macro_grids/${macro_grid_id}`),
        meal_id ? { params: { meal_id } } : undefined
      )
      .pipe(this.errorService.retryPipe(ApiType.Query));
  }

  getMenuAndQuantityRestrictions(params: {
    date: string;
    meal_id?: number;
  }): Observable<[Menu, PosQuantityRestriction[]]> {
    return forkJoin([
      this.getMenu(params),
      this.getQuantityRestrictions(params.date)
    ]);
  }

  private getMenu(params: {
    date: string;
    meal_id?: number;
  }): Observable<Menu> {
    return this.http.get<Menu>(this.getLocationUrl('menu'), {
      // DEF-14217 follow-up: dedupe a modifier_prompt shared across assignments into
      // a top-level `modifier_prompts` array instead of repeating it on each one.
      params: _.omitBy({ ...params, split_modifier_prompts: true }, _.isNil)
    });
  }

  private getQuantityRestrictions(
    date: string
  ): Observable<PosQuantityRestriction[]> {
    const apiBaseUrl = this.store.selectSnapshot(APP_STATE_TOKEN).apiBaseUrl;
    const apiUrl = AppStateHelper.getApiUrl(apiBaseUrl);
    const api = 'quantity_restrictions';
    return this.http
      .get<PosQuantityRestriction[]>(apiUrl + api, {
        params: { date }
      })
      .pipe(map((response) => response || []));
  }

  getProducts(): Observable<POSManagerProduct[]> {
    return this.fetchPosProductsForSelection<POSManagerProduct>();
  }

  createProduct(
    product: Partial<Omit<POSProductEditDto, 'price_type' | 'price'>>
  ): Observable<POSProduct> {
    if (!product) {
      throw new Error('Product can not be empty');
    }
    return this.http
      .post<POSProduct>(`${this.getPosProductsUrl()}`, product)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  updateProduct(
    id: number,
    data: Partial<POSProductEditDto>
  ): Observable<POSProduct> {
    if (!id) {
      throw new Error('Product id can not be empty');
    }
    if (!data) {
      throw new Error('Product data can not be empty');
    }

    return this.http
      .put<POSProduct>(`${this.getPosProductsUrl()}/${id}`, data)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  deleteProduct(id: number): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(`${this.getPosProductsUrl()}/${id}`)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response.success)
      );
  }

  getServices(): Observable<POSManagerService[]> {
    return this.fetchPosProductsForSelection<POSManagerService>('service');
  }

  createService(
    service: Partial<POSManagerService>
  ): Observable<POSManagerService> {
    if (!service) {
      throw new Error('Service can not be empty');
    }
    return this.http
      .post<POSManagerService>(`${this.getPosProductsUrl()}`, service)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  updateService(
    id: number,
    data: Partial<POSManagerService>
  ): Observable<POSManagerService> {
    if (!id) {
      throw new Error('Service id can not be empty');
    }
    if (!data) {
      throw new Error('Service data can not be empty');
    }
    return this.http
      .put<POSManagerService>(`${this.getPosProductsUrl()}/${id}`, data)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  deleteService(id: number): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(`${this.getPosProductsUrl()}/${id}`)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response.success)
      );
  }

  private getPosManagerBaseUrl(): string {
    const baseUrl = this.store.selectSnapshot(APP_STATE_TOKEN).apiBaseUrl;
    return AppStateHelper.getPosManagerApiUrl(baseUrl);
  }

  private getPosProductsUrl(): string {
    return `${this.getPosManagerBaseUrl()}pos_products`;
  }

  private getPosProductsForSelectionUrl(): string {
    return `${this.getPosManagerBaseUrl()}pos_products/for_selection`;
  }

  private fetchPosProducts<T>(): Observable<T[]> {
    return this.http
      .get<{ products: T[] }>(`${this.getPosProductsUrl()}`)
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.products)
      );
  }

  private fetchPosProductsForSelection<T>(type?: string): Observable<T[]> {
    return this.http
      .get<{ products: T[] }>(this.getPosProductsForSelectionUrl(), {
        params: _.omitBy({ type }, _.isNil)
      })
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.products)
      );
  }
}
